import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Badge, Input, Modal, Tabs } from '../../../components/ui';
import { Wallet, Calendar, RefreshCw, Plus, ArrowDownCircle, ArrowUpCircle, DollarSign, Store, ChevronDown, ChevronRight, FileText, Clock, Archive, AlertTriangle, Database } from 'lucide-react';
import { MantenimientoDatosCard } from '../components/MantenimientoDatosCard';
import { fetchAPI } from '../../../services/apiClient';
import { cargarSaldosBanca } from '../services/cargaSaldosBanca';
import { fechaLocalYMD } from '../../../utils/date';
import { getSaldoTone, getSaldoTextColor, getSaldoBgColor } from '../../../utils/saldoColor';
import { useAuth } from '../../../app/auth';

// Tipos
interface SaldoLocal {
  local_id: string;
  local_nombre: string;
  grupo_id?: string | null;
  saldo_actual: string;
  saldo_real?: string;
  saldo_post_sorteo?: string;
  updated_at: string | null;
  ultimo_movimiento: {
    tipo: string;
    fecha: string;
    monto: string;
  } | null;
  es_propio?: boolean;
  archivado?: boolean;
  eliminado_definitivo?: boolean;
  activo?: boolean;
  limite_saldo?: string | null;
  credito?: {
    aplica_limite_saldo: boolean;
    bloqueado_por_saldo: boolean;
    nivel_alerta: string | null;
    disponible: string | null;
    saldo_post_sorteo: string | null;
    saldo_real: string | null;
    limite_saldo: string | null;
    mensaje_bloqueo: string | null;
  } | null;
}

interface SaldoGrupo {
  grupo: string;
  ventas: number;
  premios: number;
  saldo: number;
}

interface PreviewLiquidacion {
  local_id: string;
  local_nombre: string;
  fecha: string;
  total_vendido: string;
  total_premios: string;
  total_comision: string;
  neto_dia: string;
  saldo_actual: string;
  sorteos: { sorteo: string; vendido: string; premios: string; comision: string }[];
}

interface PreviewCierre {
  local_id: string;
  local_nombre: string;
  fecha_hasta: string;
  saldo_actual: string;
  movimientos_a_eliminar: number;
  movimientos_posteriores: number;
  ya_consolidado: boolean;
}

interface Movimiento {
  id: string;
  local_id: string;
  local_nombre: string;
  tipo: string;
  monto: string;
  referencia: string;
  fecha: string;
  saldo_resultante: string;
  detalle: {
    vendido?: string;
    premios?: string;
    comision?: string;
    sorteos?: { sorteo: string; vendido: string; premios: string; comision: string }[];
    movimientos_consolidados?: number;
    fecha_desde?: string;
    fecha_hasta?: string;
  } | null;
  created_by: string;
  created_at: string;
}

interface MovimientosPaginados {
  items: Movimiento[];
  total_count: number;
  pagina: number;
  por_pagina: number;
}

const TIPO_LABELS: Record<string, string> = {
  LIQUIDACION_DIA: 'Liquidación',
  CIERRE_CONSOLIDADO: 'Cierre',
  ABONO: 'Abono',
  PAGO: 'Pago',
};

const TIPO_BADGE: Record<string, 'primary' | 'success' | 'warning' | 'danger' | 'neutral'> = {
  LIQUIDACION_DIA: 'primary',
  CIERRE_CONSOLIDADO: 'neutral',
  ABONO: 'success',
  PAGO: 'warning',
};

const formatMonto = (val: string) => {
  const n = Number(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('es-CR', { minimumFractionDigits: 0 });
};

// ========================
// TAB 1: SALDOS
// ========================
const SaldosTab: React.FC = () => {
  const navigate = useNavigate();
  // MISMA experiencia para BANCA y MINI_BANCA: la Mini Banca ve los saldos
  // de SUS vendedores internos (más su propio local) como una banca interna.
  const { user } = useAuth();
  const esMiniBanca = user?.role === 'MINI_BANCA';
  const [saldos, setSaldos] = useState<SaldoLocal[]>([]);
  const [saldosGrupo, setSaldosGrupo] = useState<SaldoGrupo[]>([]);
  const [vista, setVista] = useState<'local' | 'grupo' | 'historico'>('local');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendientes, setPendientes] = useState<{id:string;nombre:string;fecha:string}[]>([]);
  // MINI_BANCA: lo que la banca ve para esta Mini Banca (relación A: BANCA↔MB)
  const [consolidadoBanca, setConsolidadoBanca] = useState<{ saldo_actual: string; limite_saldo: string | null } | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTipo, setModalTipo] = useState<'ABONO' | 'PAGO'>('ABONO');
  const [localSeleccionado, setLocalSeleccionado] = useState<SaldoLocal | null>(null);
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [guardando, setGuardando] = useState(false);

  const [historialOpen, setHistorialOpen] = useState(false);
  const [historialLocal, setHistorialLocal] = useState<SaldoLocal | null>(null);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);

  // CARGA PROGRESIVA
  // Antes, las tres solicitudes iban en un único `Promise.all` y la tabla no
  // aparecía hasta que terminaba la más lenta. Ahora `/saldos` gobierna la
  // tabla y las fuentes secundarias (grupos y pendientes) se resuelven aparte:
  // si una falla o tarda, la tabla ya está visible.
  //
  // `vivoRef` evita actualizar estado después de desmontar el componente.
  const vivoRef = useRef(true);
  useEffect(() => {
    vivoRef.current = true;
    return () => { vivoRef.current = false; };
  }, []);

  const cargarSaldos = useCallback(async () => {
    setLoading(true);
    setError('');

    if (esMiniBanca) {
      // Fuente scoped: saldos de SUS vendedores internos + su propio local,
      // con el MISMO cálculo que usa banca (saldo real / post-sorteo /
      // crédito por vendedor) + consolidado hacia banca.
      try {
        const data = await fetchAPI<{
          mini_banca: any; vendedores: any[]; total_consolidado: string;
          consolidado_banca?: { saldo_actual: string; limite_saldo: string | null };
        }>('/saldos/minibanca/vendedores');
        if (!vivoRef.current) return;
        const aFila = (v: any): SaldoLocal => ({
          ...v,
          local_nombre: v.es_mini_banca ? `${v.local_nombre} (mi local)` : v.local_nombre,
        } as unknown as SaldoLocal);
        setSaldos([aFila(data.mini_banca), ...data.vendedores.map(aFila)]);
        setConsolidadoBanca(data.consolidado_banca ?? null);
        setSaldosGrupo([]);
        setPendientes([]);
      } catch (e: any) {
        if (vivoRef.current) setError(e.message || 'Error al cargar saldos');
      } finally {
        if (vivoRef.current) setLoading(false);
      }
      return;
    }

    // `/saldos` gobierna la tabla; grupos y pendientes se resuelven aparte.
    await cargarSaldosBanca<SaldoLocal, SaldoGrupo>({
      vivo: () => vivoRef.current,
      onSaldos: setSaldos,
      onError: setError,
      onFinPrincipal: () => setLoading(false),
      onGrupos: setSaldosGrupo,
      onPendientes: (pend) => setPendientes(pend as typeof pendientes),
    }, { fetchGrupos: () => fetchAPI<SaldoGrupo[]>('/banca/saldo/grupos?solo_operativos=true') });
  }, [esMiniBanca]);

  useEffect(() => {
    cargarSaldos();
  }, [cargarSaldos]);

  const abrirModal = (local: SaldoLocal, tipo: 'ABONO' | 'PAGO') => {
    setLocalSeleccionado(local);
    setModalTipo(tipo);
    setMonto('');
    setReferencia('');
    setError('');
    setModalOpen(true);
  };

  const guardar = async () => {
    if (!localSeleccionado || !monto) return;
    setGuardando(true);
    setError('');
    try {
      const endpoint = modalTipo === 'ABONO' ? '/saldos/abono' : '/saldos/pago';
      await fetchAPI<any>(endpoint, {
        method: 'POST',
        body: JSON.stringify({
          local_id: localSeleccionado.local_id,
          monto: parseFloat(monto),
          referencia: referencia || undefined,
        }),
      });
      setModalOpen(false);
      cargarSaldos();
    } catch (e: any) {
      setError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const abrirHistorial = async (local: SaldoLocal) => {
    setHistorialLocal(local);
    setHistorialOpen(true);
    setLoadingHistorial(true);
    try {
      const data = await fetchAPI<MovimientosPaginados>(`/saldos/movimientos?local_id=${local.local_id}&tipos=ABONO,PAGO&por_pagina=50&pagina=1`);
      setMovimientos(data.items);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingHistorial(false);
    }
  };

  const saldosVisibles = saldos.filter((item) => {
    const historico = !!(item.archivado || item.eliminado_definitivo);
    return vista === 'historico' ? historico : !historico;
  });
  const resumenActual = vista === 'grupo'
    ? saldosGrupo.map((item) => Number(item.saldo))
    : saldosVisibles.map((item) => Number(item.saldo_post_sorteo ?? item.saldo_actual));

  const totalDeben = resumenActual.reduce((acc, valor) => (valor > 0 ? acc + valor : acc), 0);
  const totalDebemos = resumenActual.reduce((acc, valor) => (valor < 0 ? acc + Math.abs(valor) : acc), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Saldos</h1>
          <p className="text-gray-500">Consulta por local o agrupado por grupo de locales</p>
        </div>
        <div className="flex items-center gap-2">
          {!esMiniBanca && (
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
              <button onClick={() => setVista('local')} className={`px-3 py-1.5 text-sm rounded-md ${vista === 'local' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Operativos</button>
              <button onClick={() => setVista('grupo')} className={`px-3 py-1.5 text-sm rounded-md ${vista === 'grupo' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Por grupo</button>
            </div>
          )}
          <button onClick={() => setVista(vista === 'historico' ? 'local' : 'historico')} className={`px-3 py-1.5 text-sm rounded-md ${vista === 'historico' ? 'bg-blue-600 text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>Obligaciones históricas</button>
          <button onClick={cargarSaldos} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Refrescar">
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {error && !modalOpen && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {esMiniBanca && consolidadoBanca && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <p className="text-sm text-blue-900">
            <strong>Mi consolidado hacia banca:</strong>{' '}
            ₡{Number(consolidadoBanca.saldo_actual).toLocaleString('es-CR')}
            {Number(consolidadoBanca.saldo_actual) > 0
              ? ' — su Mini Banca debe a la banca'
              : Number(consolidadoBanca.saldo_actual) < 0
                ? ' — la banca debe a su Mini Banca'
                : ' — en cero'}
            {consolidadoBanca.limite_saldo && (
              <span className="text-blue-700"> · Límite asignado por banca: ₡{Number(consolidadoBanca.limite_saldo).toLocaleString('es-CR')}</span>
            )}
          </p>
          <p className="text-xs text-blue-700">La banca ve su Mini Banca como un solo local con esta cifra.</p>
        </div>
      )}

      {pendientes.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              <strong>{pendientes.length} sorteo(s) sin ganador.</strong> Esto causa diferencias entre saldo real y post-sorteo.
            </p>
          </div>
          <button
            onClick={() => navigate('/banca/premios')}
            className="w-full sm:w-auto px-3 py-2 bg-amber-600 text-white rounded text-xs font-medium hover:bg-amber-700 whitespace-nowrap text-center"
          >
            Asignar ganadores
          </button>
        </div>
      )}

      <p className="text-sm text-gray-600">{vista === 'historico'
        ? 'Obligaciones e historial de entidades archivadas o eliminadas definitivamente. Sus saldos se conservan.'
        : 'Totales operativos de entidades actuales, incluidos los locales inactivos reactivables.'}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <ArrowUpCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Nos Deben</p>
              <p className="text-2xl font-bold text-green-600">{formatMonto(String(totalDeben))}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <ArrowDownCircle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Debemos</p>
              <p className="text-2xl font-bold text-red-600">{formatMonto(String(totalDebemos))}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Saldo Neto</p>
              <p className={`text-2xl font-bold ${getSaldoTextColor(getSaldoTone(totalDeben - totalDebemos))}`}>
                {formatMonto(String(totalDeben - totalDebemos))}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : vista !== 'grupo' ? (
          saldosVisibles.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay saldos en esta vista.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Local</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Grupo</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Ultimo Mov.</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {saldosVisibles.map((s) => {
                    const saldoPost = Number(s.saldo_post_sorteo ?? s.saldo_actual);
                    const saldoReal = Number(s.saldo_real ?? s.saldo_actual);
                    const hayDiferencia = Math.abs(saldoReal - saldoPost) >= 1;
                    return (
                      <tr key={s.local_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Store size={16} className="text-gray-400" />
                            <span className="font-medium text-gray-900">{s.local_nombre}{s.eliminado_definitivo ? ' — Histórico' : s.archivado ? ' — Archivado' : s.activo === false ? ' — Inactivo' : ''}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{s.grupo_id || 'Sin grupo'}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`text-lg font-bold ${getSaldoTextColor(getSaldoTone(saldoPost))}`}>
                            {formatMonto(s.saldo_post_sorteo ?? s.saldo_actual)}
                          </span>
                          <p className="text-[10px] text-gray-400 uppercase tracking-wide">Saldo post-sorteo</p>
                          {saldoPost > 0 && <p className="text-xs text-green-500">Debe a banca</p>}
                          {saldoPost < 0 && <p className="text-xs text-red-500">Banca debe</p>}
                          {hayDiferencia && (
                            <div className="mt-1.5 pt-1.5 border-t border-dashed border-gray-300">
                              <span className={`text-sm font-semibold ${saldoReal > 0 ? 'text-green-500' : saldoReal < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                                {formatMonto(s.saldo_real ?? s.saldo_actual)}
                              </span>
                              <p className="text-[10px] text-gray-400 uppercase tracking-wide">Saldo real</p>
                            </div>
                          )}
                          {!s.es_propio && s.credito?.aplica_limite_saldo && (() => {
                            const c = s.credito!;
                            const bloqueado = c.bloqueado_por_saldo;
                            const disponible = Number(c.disponible || 0);
                            const limite = Number(c.limite_saldo || 0);
                            const pctRest = limite > 0 ? Math.round((disponible / limite) * 100) : 100;
                            const color = bloqueado ? 'text-red-700 font-bold' : pctRest <= 5 ? 'text-red-600' : pctRest <= 10 ? 'text-red-500' : pctRest <= 20 ? 'text-amber-600' : 'text-gray-500';
                            return (
                              <p className={`text-xs mt-0.5 ${color} ${!bloqueado && pctRest <= 5 ? 'animate-pulse' : ''}`}>
                                {bloqueado ? 'BLOQUEADO' : `Disp: ${formatMonto(String(disponible))}`} / Lím: {formatMonto(String(limite))}
                              </p>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {s.ultimo_movimiento ? (
                            <div className="text-sm">
                              <Badge variant={TIPO_BADGE[s.ultimo_movimiento.tipo] || 'neutral'}>
                                {TIPO_LABELS[s.ultimo_movimiento.tipo] || s.ultimo_movimiento.tipo}
                              </Badge>
                              <p className="text-xs text-gray-400 mt-1">{s.ultimo_movimiento.fecha}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col sm:flex-row gap-1 justify-center">
                            {/* Abono/Pago:
                                BANCA → relación banca↔local (o banca↔MB, libro A).
                                MINI_BANCA → relación interna MB↔vendedor (libro B),
                                solo sobre sus vendedores (no sobre su fila propia). */}
                            {!(esMiniBanca && (s as any).es_mini_banca) && (
                              <>
                                <button onClick={() => abrirModal(s, 'ABONO')} className="px-3 py-2 sm:py-1.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-lg border border-green-200">Me Pagan</button>
                                <button onClick={() => abrirModal(s, 'PAGO')} className="px-3 py-2 sm:py-1.5 text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200">Yo Pago</button>
                              </>
                            )}
                            <button onClick={() => abrirHistorial(s)} className="px-3 py-2 sm:py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200">Historial</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : (
          saldosGrupo.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay grupos configurados para esta banca.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Grupo</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Ventas</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Premios</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {saldosGrupo.map((grupo) => {
                    const saldo = Number(grupo.saldo);
                    return (
                      <tr key={grupo.grupo} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{grupo.grupo}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">{formatMonto(String(grupo.ventas))}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-700">{formatMonto(String(grupo.premios))}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`text-lg font-bold ${getSaldoTextColor(getSaldoTone(saldo))}`}>
                            {formatMonto(String(grupo.saldo))}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </Card>

      {/* Modal Abono/Pago */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTipo === 'ABONO' ? 'Registrar Abono' : 'Registrar Pago'}
      >
        <div className="space-y-4">
          {localSeleccionado && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Local:</p>
              <p className="font-semibold">{localSeleccionado.local_nombre}</p>
              <p className="text-sm text-gray-500 mt-1">Saldo post-sorteo:</p>
              <p className={`font-bold ${getSaldoTextColor(getSaldoTone(Number(localSeleccionado.saldo_post_sorteo ?? localSeleccionado.saldo_actual)))}`}>
                {formatMonto(localSeleccionado.saldo_post_sorteo ?? localSeleccionado.saldo_actual)}
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {modalTipo === 'ABONO' ? 'Monto del Abono' : 'Monto del Pago'}
            </label>
            <input
              type="number"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0"
              min={0}
              step={1}
              className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Referencia (opcional)</label>
            <input
              type="text"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Ej: Deposito bancario, Efectivo..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
          )}

          <div className="flex gap-2 justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button
              variant={modalTipo === 'ABONO' ? 'success' : 'warning'}
              onClick={guardar}
              disabled={guardando || !monto || parseFloat(monto) <= 0}
            >
              {guardando ? 'Guardando...' : modalTipo === 'ABONO' ? 'Registrar Abono' : 'Registrar Pago'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Historial */}
      <Modal
        isOpen={historialOpen}
        onClose={() => setHistorialOpen(false)}
        title={`Historial - ${historialLocal?.local_nombre || ''}`}
        size="lg"
      >
        <div className="space-y-4">
          {loadingHistorial ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : movimientos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">Sin movimientos</div>
          ) : (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="py-2 pr-2">Fecha</th>
                    <th className="py-2 pr-2">Tipo</th>
                    <th className="py-2 pr-2 text-right">Monto</th>
                    <th className="py-2 pr-2 text-right">Saldo</th>
                    <th className="py-2">Referencia</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map(m => (
                    <tr key={m.id} className="border-b last:border-0">
                      <td className="py-2 pr-2 text-xs">{m.fecha}</td>
                      <td className="py-2 pr-2">
                        <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                          {TIPO_LABELS[m.tipo] || m.tipo}
                        </Badge>
                      </td>
                      <td className={`py-2 pr-2 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                      </td>
                      <td className={`py-2 pr-2 text-right font-bold ${getSaldoTextColor(getSaldoTone(Number(m.saldo_resultante)))}`}>
                        {formatMonto(m.saldo_resultante)}
                      </td>
                      <td className="py-2 text-xs text-gray-500">{m.referencia}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};


// ========================
// TAB 2: LIQUIDACION DIARIA
// ========================
const LiquidacionTab: React.FC = () => {
  const [fechaLiq, setFechaLiq] = useState(fechaLocalYMD);
  const [locales, setLocales] = useState<{ id: string; nombre: string }[]>([]);
  const [localSeleccionado, setLocalSeleccionado] = useState('');
  const [preview, setPreview] = useState<PreviewLiquidacion | null>(null);
  const [loading, setLoading] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  // Cargar locales
  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchAPI<{ id: string; nombre: string }[]>('/reportes/locales?solo_operativos=true');
        setLocales(data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    cargar();
  }, []);

  const cargarPreview = async () => {
    if (!localSeleccionado) return;
    setLoading(true);
    setError('');
    setMensaje('');
    setPreview(null);
    try {
      const data = await fetchAPI<PreviewLiquidacion>(
        `/saldos/preview-liquidacion?local_id=${localSeleccionado}&fecha=${fechaLiq}`
      );
      setPreview(data);
    } catch (e: any) {
      setError(e.message || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };

  const ejecutarLiquidacion = async () => {
    if (!localSeleccionado) return;
    setEjecutando(true);
    setError('');
    setMensaje('');
    try {
      const result = await fetchAPI<any>('/saldos/liquidacion', {
        method: 'POST',
        body: JSON.stringify({
          local_id: localSeleccionado,
          fecha: fechaLiq,
        }),
      });
      setMensaje(`Resumen del dia: Neto ${formatMonto(result.neto_dia)}. Saldo actual: ${formatMonto(result.saldo_nuevo)}`);
      setPreview(null);
    } catch (e: any) {
      setError(e.message || 'Error al ejecutar liquidación');
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Liquidación Diaria</h1>
        <p className="text-gray-500">Resumen informativo: ventas - comisiones - premios del dia</p>
      </div>

      {/* Controles */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
            <Input
              type="date"
              value={fechaLiq}
              onChange={(e) => setFechaLiq(e.target.value)}
              leftIcon={<Calendar size={18} />}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={localSeleccionado}
              onChange={(e) => setLocalSeleccionado(e.target.value)}
            >
              <option value="">Seleccione un local</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            onClick={cargarPreview}
            disabled={!localSeleccionado || loading}
          >
            {loading ? 'Calculando...' : 'Calcular'}
          </Button>
        </div>
      </Card>

      {/* Mensajes */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}
      {mensaje && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>
      )}

      {/* Preview */}
      {preview && (
        <Card>
          <div className="space-y-4">
            {/* Header info */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{preview.local_nombre}</h3>
                <p className="text-sm text-gray-500">Fecha: {preview.fecha}</p>
              </div>
            </div>

            {/* Desglose */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-600 font-medium">Ventas</p>
                <p className="text-lg font-bold text-blue-700">{formatMonto(preview.total_vendido)}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-600 font-medium">Comisiones</p>
                <p className="text-lg font-bold text-yellow-700">-{formatMonto(preview.total_comision)}</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-xs text-red-600 font-medium">Premios</p>
                <p className="text-lg font-bold text-red-700">-{formatMonto(preview.total_premios)}</p>
              </div>
              <div className={`p-3 rounded-lg ${Number(preview.neto_dia) >= 0 ? 'bg-green-50' : 'bg-orange-50'}`}>
                <p className={`text-xs font-medium ${Number(preview.neto_dia) >= 0 ? 'text-green-600' : 'text-orange-600'}`}>Neto del Dia</p>
                <p className={`text-lg font-bold ${Number(preview.neto_dia) >= 0 ? 'text-green-700' : 'text-orange-700'}`}>
                  {formatMonto(preview.neto_dia)}
                </p>
              </div>
            </div>

            {/* Saldo actual */}
            <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Saldo actual del local</p>
                  <p className={`text-xl font-bold ${getSaldoTextColor(getSaldoTone(Number(preview.saldo_actual)))}`}>
                    {formatMonto(preview.saldo_actual)}
                  </p>
                </div>
              </div>
            </div>

            {/* Detalle sorteos */}
            {preview.sorteos.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Detalle por sorteo:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-2">Sorteo</th>
                        <th className="py-2 pr-2 text-right">Vendido</th>
                        <th className="py-2 pr-2 text-right">Premios</th>
                        <th className="py-2 text-right">Comision</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.sorteos.map((s, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 pr-2 font-medium">{s.sorteo}</td>
                          <td className="py-2 pr-2 text-right">{formatMonto(s.vendido)}</td>
                          <td className="py-2 pr-2 text-right">{formatMonto(s.premios)}</td>
                          <td className="py-2 text-right">{formatMonto(s.comision)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Boton ver resumen confirmado */}
            <div className="pt-4 border-t">
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={ejecutarLiquidacion}
                  disabled={ejecutando}
                  leftIcon={<DollarSign size={18} />}
                >
                  {ejecutando ? 'Consultando...' : 'Confirmar Resumen'}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};


// ========================
// TAB 3: CIERRE CONSOLIDADO
// ========================
// ========================
// TAB: CIERRE — RETIRADO
// ========================
// El cierre consolidado insertaba un CIERRE_CONSOLIDADO con el saldo completo
// del local pero nunca eliminaba los movimientos que resumía, duplicando la
// suma y descuadrando el local. Es el origen de los descuadres actuales.
// La consolidación correcta la hace Mantenimiento → Limpieza de datos, que
// crea el asiento de apertura con el monto exacto de lo que borra.
// El backend además responde 409 si alguien llama al endpoint directamente.
const CierreTab: React.FC = () => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Cierre consolidado</h1>
      <p className="text-gray-500">Función retirada</p>
    </div>
    <Card>
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-100 rounded-lg shrink-0">
          <AlertTriangle size={20} className="text-amber-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">
            Sustituido por Mantenimiento → Limpieza de datos
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            El cierre consolidado resumía el historial pero no lo eliminaba, así
            que la suma de movimientos quedaba duplicada y el local aparecía
            descuadrado. Se retiró para que eso no vuelva a ocurrir.
          </p>
          <p className="text-sm text-gray-600 mt-2">
            Use la pestaña <strong>Mantenimiento</strong>: concilia el historial
            y luego lo consolida creando el asiento de apertura con el monto
            exacto de lo que depura, verificando que ningún saldo cambie.
          </p>
        </div>
      </div>
    </Card>
  </div>
);

const CierreTabLegacy: React.FC = () => {
  const [fechaHasta, setFechaHasta] = useState(fechaLocalYMD);
  const [locales, setLocales] = useState<{ id: string; nombre: string }[]>([]);
  const [localSeleccionado, setLocalSeleccionado] = useState('');
  const [preview, setPreview] = useState<PreviewCierre | null>(null);
  const [loading, setLoading] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchAPI<{ id: string; nombre: string }[]>('/reportes/locales?solo_operativos=true');
        setLocales(data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    cargar();
  }, []);

  const cargarPreview = async () => {
    if (!localSeleccionado) return;
    setLoading(true);
    setError('');
    setMensaje('');
    setPreview(null);
    try {
      const data = await fetchAPI<PreviewCierre>(
        `/saldos/preview-cierre?local_id=${localSeleccionado}&fecha_hasta=${fechaHasta}`
      );
      setPreview(data);
    } catch (e: any) {
      setError(e.message || 'Error al calcular');
    } finally {
      setLoading(false);
    }
  };

  const ejecutarCierre = async () => {
    if (!localSeleccionado) return;
    setEjecutando(true);
    setError('');
    setMensaje('');
    try {
      const result = await fetchAPI<any>('/saldos/cierre', {
        method: 'POST',
        body: JSON.stringify({
          local_id: localSeleccionado,
          fecha_hasta: fechaHasta,
        }),
      });
      setMensaje(
        `Cierre ejecutado. ${result.movimientos_eliminados} movimientos consolidados. ` +
        `Saldo: ${formatMonto(result.saldo_actual)} (sin cambios)`
      );
      setPreview(null);
    } catch (e: any) {
      setError(e.message || 'Error al ejecutar cierre');
    } finally {
      setEjecutando(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cierre Consolidado</h1>
        <p className="text-gray-500">Limpieza administrativa del historial. El saldo no cambia.</p>
      </div>

      {/* Info box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Operación técnica:</strong> Consolida todos los movimientos anteriores a la fecha seleccionada
          en un solo registro resumen. Los movimientos antiguos se eliminan pero el saldo permanece exactamente igual.
          Útil para limpiar historial acumulado.
        </p>
      </div>

      {/* Controles */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Consolidar hasta fecha</label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              leftIcon={<Calendar size={18} />}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={localSeleccionado}
              onChange={(e) => setLocalSeleccionado(e.target.value)}
            >
              <option value="">Seleccione un local</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          <Button
            variant="primary"
            onClick={cargarPreview}
            disabled={!localSeleccionado || loading}
          >
            {loading ? 'Calculando...' : 'Preview'}
          </Button>
        </div>
      </Card>

      {/* Mensajes */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}
      {mensaje && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">{mensaje}</div>
      )}

      {/* Preview */}
      {preview && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">{preview.local_nombre}</h3>
                <p className="text-sm text-gray-500">Consolidar hasta: {preview.fecha_hasta}</p>
              </div>
              {preview.ya_consolidado && (
                <Badge variant="warning">Ya consolidado</Badge>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-600 font-medium">Movimientos a eliminar</p>
                <p className="text-2xl font-bold text-orange-700">{preview.movimientos_a_eliminar}</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600 font-medium">Movimientos que quedan</p>
                <p className="text-2xl font-bold text-blue-700">{preview.movimientos_posteriores}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-300">
                <p className="text-xs text-gray-600 font-medium">Saldo (no cambia)</p>
                <p className={`text-2xl font-bold ${getSaldoTextColor(getSaldoTone(Number(preview.saldo_actual)))}`}>
                  {formatMonto(preview.saldo_actual)}
                </p>
              </div>
            </div>

            {!preview.ya_consolidado && preview.movimientos_a_eliminar > 0 && (
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="danger"
                  onClick={ejecutarCierre}
                  disabled={ejecutando}
                  leftIcon={<Archive size={18} />}
                >
                  {ejecutando ? 'Consolidando...' : 'Ejecutar Cierre'}
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};


// ========================
// TAB 4: HISTORIAL
// ========================
const HistorialTab: React.FC = () => {
  const [localId, setLocalId] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [locales, setLocales] = useState<{ id: string; nombre: string }[]>([]);
  const [movimientos, setMovimientos] = useState<Movimiento[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [pagina, setPagina] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const porPagina = 50;

  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await fetchAPI<{ id: string; nombre: string }[]>('/reportes/locales');
        setLocales(data);
      } catch (e: any) {
        setError(e.message);
      }
    };
    cargar();
  }, []);

  const cargarMovimientos = async (pag: number = pagina) => {
    setLoading(true);
    setError('');
    try {
      let url = `/saldos/movimientos?pagina=${pag}&por_pagina=${porPagina}`;
      if (localId) url += `&local_id=${localId}`;
      if (fechaDesde) url += `&fecha_desde=${fechaDesde}`;
      if (fechaHasta) url += `&fecha_hasta=${fechaHasta}`;
      const data = await fetchAPI<MovimientosPaginados>(url);
      setMovimientos(data.items);
      setTotalCount(data.total_count);
      setPagina(data.pagina);
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarMovimientos(1);
  }, []);

  const buscar = () => {
    setPagina(1);
    cargarMovimientos(1);
  };

  const irPagina = (pag: number) => {
    cargarMovimientos(pag);
  };

  const totalPaginas = Math.max(1, Math.ceil(totalCount / porPagina));

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Historial de Movimientos</h1>
        <p className="text-gray-500">Todos los movimientos de saldo</p>
      </div>

      {/* Filtros */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Local</label>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={localId}
              onChange={(e) => setLocalId(e.target.value)}
            >
              <option value="">Todos los locales</option>
              {locales.map(l => (
                <option key={l.id} value={l.id}>{l.nombre}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <Input
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <Input
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />
          </div>
          <Button variant="primary" onClick={buscar} disabled={loading}>
            {loading ? 'Cargando...' : 'Buscar'}
          </Button>
        </div>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Tabla */}
      <Card padding="none">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Cargando...</div>
        ) : movimientos.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Sin movimientos</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700 w-8"></th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Fecha</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Local</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-gray-700">Tipo</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Monto</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-700">Saldo</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-700">Referencia</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {movimientos.map(m => {
                  const isExpanded = expandedRows.has(m.id);
                  const hasDetail = m.detalle && (m.detalle.vendido || m.detalle.movimientos_consolidados);
                  return (
                    <React.Fragment key={m.id}>
                      <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => hasDetail && toggleRow(m.id)}>
                        <td className="px-4 py-3">
                          {hasDetail && (
                            isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
                          )}
                        </td>
                        <td className="px-4 py-3">{m.fecha}</td>
                        <td className="px-4 py-3 font-medium">{m.local_nombre}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant={TIPO_BADGE[m.tipo] || 'neutral'}>
                            {TIPO_LABELS[m.tipo] || m.tipo}
                          </Badge>
                        </td>
                        <td className={`px-4 py-3 text-right font-mono font-medium ${Number(m.monto) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {Number(m.monto) >= 0 ? '+' : ''}{formatMonto(m.monto)}
                        </td>
                        <td className={`px-4 py-3 text-right font-bold ${getSaldoTextColor(getSaldoTone(Number(m.saldo_resultante)))}`}>
                          {formatMonto(m.saldo_resultante)}
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">{m.referencia}</td>
                      </tr>
                      {isExpanded && m.detalle && (
                        <tr>
                          <td colSpan={7} className="px-8 py-3 bg-gray-50">
                            {/* Liquidación detail */}
                            {m.detalle.vendido && (
                              <>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-2">
                                  <div>
                                    <p className="text-xs text-gray-500">Ventas</p>
                                    <p className="font-medium">{formatMonto(m.detalle.vendido)}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Premios</p>
                                    <p className="font-medium">-{formatMonto(m.detalle.premios || '0')}</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-gray-500">Comisiones</p>
                                    <p className="font-medium">-{formatMonto(m.detalle.comision || '0')}</p>
                                  </div>
                                </div>
                                {m.detalle.sorteos && m.detalle.sorteos.length > 0 && (
                                  <table className="w-full text-xs mt-2">
                                    <thead>
                                      <tr className="text-gray-500 border-b">
                                        <th className="text-left py-1">Sorteo</th>
                                        <th className="text-right py-1">Vendido</th>
                                        <th className="text-right py-1">Premios</th>
                                        <th className="text-right py-1">Comision</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {m.detalle.sorteos.map((s, i) => (
                                        <tr key={i}>
                                          <td className="py-1">{s.sorteo}</td>
                                          <td className="text-right py-1">{formatMonto(s.vendido)}</td>
                                          <td className="text-right py-1">{formatMonto(s.premios)}</td>
                                          <td className="text-right py-1">{formatMonto(s.comision)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </>
                            )}
                            {/* Cierre consolidado detail */}
                            {m.detalle.movimientos_consolidados && (
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs text-gray-500">Movimientos consolidados</p>
                                  <p className="font-medium">{m.detalle.movimientos_consolidados}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Rango</p>
                                  <p className="font-medium">{m.detalle.fecha_desde} a {m.detalle.fecha_hasta}</p>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Paginación */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <p className="text-sm text-gray-500">
            {totalCount} movimiento{totalCount !== 1 ? 's' : ''} — Página {pagina} de {totalPaginas}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina <= 1 || loading}
              onClick={() => irPagina(pagina - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina >= totalPaginas || loading}
              onClick={() => irPagina(pagina + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


// ========================
// COMPONENTE PRINCIPAL CON TABS
// ========================
// ========================
// TAB: MANTENIMIENTO DE DATOS
// ========================
const MantenimientoTab: React.FC = () => {
  // La conciliación es requisito de la limpieza: al terminar, se fuerza a
  // revisar de nuevo para que la tarjeta de limpieza refleje el nuevo estado.
  const [version, setVersion] = React.useState(0);
  const refrescar = () => {
    setVersion((v) => v + 1);
    window.dispatchEvent(new Event('tiemposb:saldos-actualizados'));
  };
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mantenimiento</h1>
        <p className="text-gray-500">
          Depuración de información operativa antigua.
          No afecta saldos ni configuración.
        </p>
      </div>
      <MantenimientoDatosCard key={version} onLimpiezaCompleta={refrescar} />
    </div>
  );
};

export const SaldosPage: React.FC = () => {
  const { user } = useAuth();
  // Liquidación y Cierre son procesos financieros de BANCA (cierre diario):
  // no se habilitan para MINI_BANCA en esta fase.
  const esMiniBanca = user?.role === 'MINI_BANCA';
  return (
    <Tabs
      items={[
        {
          key: 'saldos',
          label: 'Saldos',
          icon: <Wallet size={16} />,
          content: <SaldosTab />,
        },
        ...(esMiniBanca ? [] : [
          {
            key: 'liquidacion',
            label: 'Liquidación',
            icon: <DollarSign size={16} />,
            content: <LiquidacionTab />,
          },
          {
            key: 'cierre',
            label: 'Cierre',
            icon: <Archive size={16} />,
            content: <CierreTab />,
          },
          {
            key: 'mantenimiento',
            label: 'Mantenimiento',
            icon: <Database size={16} />,
            content: <MantenimientoTab />,
          },
        ]),
        {
          key: 'historial',
          label: 'Historial',
          icon: <FileText size={16} />,
          content: <HistorialTab />,
        },
      ]}
    />
  );
};

export default SaldosPage;
